/**
 * Monat je Apartment: sechs Wochen ab dem Montag vor dem Ersten (42 Tage, das Lesemodell trägt
 * 90), Zellen aus demselben Zellmodell wie der Grundkalender und Balken je Wochenzeile von der
 * Anreise am Nachmittag bis zur Abreise am Morgen.
 */
import type {
  AvailabilityDistributionApartment,
  AvailabilityDistributionChannelCloseout,
} from '@/shared/api';
import { addIsoDays, parseIso } from '../calendar/calendarDates';
import { buildApartmentModel, type BookingBar, type DayCellModel } from '../calendar/calendarModel';

export const MONTH_DAYS = 42;
// Feste Kürzel statt Intl: Browser liefern „Sep“ oder „Sept.“ je nach Umgebung.
export const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mär',
  'Apr',
  'Mai',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Okt',
  'Nov',
  'Dez',
];

/** „2026-10“ */
export function isMonth(value: unknown): value is string {
  return typeof value === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(value);
}

export function monthOf(iso: string): string {
  return iso.slice(0, 7);
}

export function shiftMonth(month: string, delta: number): string {
  const date = parseIso(`${month}-01`);
  date.setUTCMonth(date.getUTCMonth() + delta);
  return date.toISOString().slice(0, 7);
}

export function monthWindow(month: string): { from: string; to: string } {
  const first = parseIso(`${month}-01`);
  // Montag = 0: getUTCDay liefert Sonntag = 0.
  const offset = (first.getUTCDay() + 6) % 7;
  const from = addIsoDays(`${month}-01`, -offset);
  return { from, to: addIsoDays(from, MONTH_DAYS - 1) };
}

export function monthTitle(month: string): string {
  return new Intl.DateTimeFormat('de-DE', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(parseIso(`${month}-01`));
}

export interface MonthWeekBar {
  bar: BookingBar;
  /** Lage in der Woche in Tagesspalten, halbe Tage für Anreise und Abreise. */
  start: number;
  end: number;
  cutStart: boolean;
  cutEnd: boolean;
}

export interface MonthWeek {
  cells: DayCellModel[];
  bars: MonthWeekBar[];
}

export function buildMonth(
  apartment: AvailabilityDistributionApartment,
  closeouts: readonly AvailabilityDistributionChannelCloseout[],
): MonthWeek[] {
  const model = buildApartmentModel(apartment, closeouts);
  return Array.from({ length: Math.ceil(model.cells.length / 7) }, (_, row) => {
    const first = row * 7;
    const last = first + 7;
    const bars = model.bars.flatMap((bar) => {
      const arrive = bar.clippedStart ? bar.startIndex : bar.startIndex + 0.5;
      const leave = bar.clippedEnd
        ? bar.startIndex + bar.nights
        : bar.startIndex + bar.nights + 0.5;
      if (leave <= first || arrive >= last) return [];
      return [
        {
          bar,
          start: Math.max(arrive, first) - first,
          end: Math.min(leave, last) - first,
          cutStart: arrive < first,
          cutEnd: leave > last,
        },
      ];
    });
    return { cells: model.cells.slice(first, last), bars };
  });
}
